import {
    React,
    useState,
    CustomRadioButton,
    CustomTextField,
    Typography,
    Box,
    Grid2,
    AFPTable,
    CustomConfirmDialog,
    Toaster,
    PaymentRequest,
    CustomBackdrop,
    CustomButton,
    validationMessages,
    Paper,

} from '../../utils/globalImports';
import {FilterListOutlined} from '@mui/icons-material'
import {
    useOutletContext,
    useParams,
    useApi,
    useEffect,
} from '../../utils/globalImports';

export interface ProjectPayment {
    paymentID: number;
    projectID: number;
    paymentRequest: boolean;
    isScheduledPayment: boolean;
    submissionInterval: number;
    certificationInterval: number;
    paymentInterval: number;
    certificateAmount: number;
    invoiceToPaymentDays: number;
    submissionDates: string[];
    certificationDates: string[];
    expectedPaymentDates: string[];
};

const AFP: React.FC = () => {

    const [projectData] = useOutletContext<any>();
    const [paymentData, setPaymentData] = useState<ProjectPayment | null>(null);
    const [afpDialog, setAFPDialog] = useState({
        isOpen: false,
        aFPValue: null as boolean | null,
    });

    const [requiredAFPDialog, setRequiredAFPDialog] = useState({
        isOpen: false,
        aFPRequired: null as boolean | null,
    });

    const [toast, setToast] = useState({ open: false, message: "", type: "success" });
    const [formData, setFormData] = useState({
        isSelectedAFP: false,
        isAFPTypeSchedule: null as boolean | null,
        afpToCertificate: 0,
        invoiceToPayment: 0
    });

    const [errors, setErrors] = useState({
        afpToCertificate: '',
        invoiceToPayment: '',
    });

    const { projectId } = useParams<{ projectId?: string }>(); // Get projectId from URL
    const { execute, loading } = useApi();

    useEffect(() => {
        fetchAndSetPaymentData();
    }, [projectId])


    const fetchAndSetPaymentData = async () => {
        const { data } = await fetchPaymentData();
        if (data) {
            setFormData({
                isSelectedAFP: data.paymentRequest,
                isAFPTypeSchedule: data.isScheduledPayment,
                afpToCertificate: data.certificateAmount || 0,
                invoiceToPayment: data.invoiceToPaymentDays || 0
            })
        }
        else {
            setFormData({
                isSelectedAFP: false,
                isAFPTypeSchedule: null as boolean | null,
                afpToCertificate: 0,
                invoiceToPayment: 0
            })
        }
        setPaymentData(data); // ensure new reference
    }

    // validate client form field
    const validate = () => {
        let isValid = true;
        const errors = { afpToCertificate: '', invoiceToPayment: '' };

        if (formData.afpToCertificate === 0) {
            errors.afpToCertificate = validationMessages.common_GreaterThanZeroNumber("");
            isValid = false;
        }

        if (formData.invoiceToPayment === 0) {
            errors.invoiceToPayment = validationMessages.common_GreaterThanZeroNumber("");
            isValid = false;
        }

        setErrors(errors);
        return isValid;
    };

    // handle AFP Type 'As Required'
    const handleAsRequired = async () => {
        if (validate()) {
            const isSchedule = formData.isAFPTypeSchedule!;
            const { data } = await fetchPaymentData();
            if (data)
                await updatePaymentData(isSchedule, data)
            else
                await addPaymentData(isSchedule, data)
        }
    }

    // handle AFP Type 'Schedule'
    const handleSchedule = async (payload: PaymentRequest) => {
        const isSchedule = formData.isAFPTypeSchedule!;
        if (paymentData) {

            const data: ProjectPayment = {
                ...paymentData,
                submissionInterval: payload?.submissionInterval,
                certificationInterval: payload?.certificationInterval,
                paymentInterval: payload?.expectedInterval
            }
            await updatePaymentData(isSchedule, data)
        }
        else {
            const data: ProjectPayment = {
                ...paymentData!,
                submissionInterval: payload?.submissionInterval,
                certificationInterval: payload?.certificationInterval,
                paymentInterval: payload?.expectedInterval
            }

            await addPaymentData(isSchedule, data)
        }

        const datePayload = {
            projectId: Number(projectId),
            submissionDates: payload?.submissionDates,
            certificationDates: payload?.certificationDates,
            expectedPaymentDates: payload?.expectedPaymentDates
        }

        await addScheduleDate(datePayload)
    }

    // handle AFP Type Change
    const handleAFPDialogConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            isAFPTypeSchedule: afpDialog.aFPValue
        }));
        handleAFPDialogCancel();
        // delete all the schedule [Submission, Certificate, Expected Payment] date when AFP type change 'Schedule' to 'As Required'
        if (formData.isAFPTypeSchedule) await deleteScheduleDate();

        await updatePaymentData(afpDialog.aFPValue!, paymentData)
    }

    // handle AFP Required Change
    const handleRequiredAFPDialogConfirm = async () => {
        setFormData((prev) => ({
            ...prev,
            isSelectedAFP: requiredAFPDialog.aFPRequired || false
        }));

        // delete complete payment when AFP Required No
        if (formData.isSelectedAFP) {
            await deletePayment();
        }
        handleRequiredAFPDialogCancel();
        await fetchAndSetPaymentData()
    }

    const fetchPaymentData = async () => {
        return await execute<ProjectPayment>('get', `/Project/GetProjectPaymentsWithDates/${projectId}`);
    };

    const addPaymentData = async (isSchedule: boolean, payment: any) => {
        const payload: any = {
            projectID: Number(projectId),
            paymentRequest: formData.isSelectedAFP,
            isScheduledPayment: isSchedule,
            ...(isSchedule ? {
                submissionInterval: payment?.submissionInterval,
                certificationInterval: payment?.certificationInterval,
                paymentInterval: payment?.paymentInterval
            } : {
                certificateAmount: formData.afpToCertificate,
                invoiceToPaymentDays: formData.invoiceToPayment
            })
        };
        const response = await execute('post', `/Project/AddProjectPayment/${projectId}`, payload);
        showMessage(response);

        await fetchAndSetPaymentData()
    }

    const updatePaymentData = async (isSchedule: boolean, payment: any) => {
        const payload: any = {
            paymentID: payment?.paymentID,
            projectId: payment?.projectID,
            paymentRequest: formData.isSelectedAFP,
            isScheduledPayment: isSchedule,
            ...(isSchedule ? {
                submissionInterval: payment?.submissionInterval,
                certificationInterval: payment?.certificationInterval,
                paymentInterval: payment?.paymentInterval
            } : {
                certificateAmount: formData.afpToCertificate,
                invoiceToPaymentDays: formData.invoiceToPayment
            })
        };
        const response = await execute('put', `/Project/UpdateProjectPayment`, payload);
        showMessage(response);
        await fetchAndSetPaymentData()
    }

    const addScheduleDate = async (payload: any) => {
        await execute('post', `/Project/AddPaymentDates/${projectId}`, payload);
    }

    const deleteScheduleDate = async () => {
        await execute('delete', `/Project/DeleteProjectPaymentDates/${projectId}`);
    }

    // when AFP Required 'No'
    const deletePayment = async () => {
        const response = await execute('delete', `/Project/DeleteProjectPaymentsByProjectId/${projectId}`);
        showMessage(response);
    }

    const showMessage = (response: any) => {
        if (response) {
            if (response.error) {
                setToast({ open: true, message: response.error, type: "error" });
            } else {
                setToast({ open: true, message: response.data.message, type: "success" });
            }
        }
    }

    const handleAFPDialogCancel = () => {
        setAFPDialog({
            isOpen: false,
            aFPValue: null
        })
    };

    const handleRequiredAFPDialogCancel = () => {
        setRequiredAFPDialog({
            isOpen: false,
            aFPRequired: null
        })
    };

    return (
        <Box sx={{ flexGrow: 1 }}>

             <Paper elevation={0} sx={{ border: '1px solid #e0eaf6', borderRadius: '14px', overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, backgroundColor: '#f0f6ff', borderBottom: '1px solid #e0eaf6' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <FilterListOutlined sx={{ fontSize: 18, color: '#1e66ab' }} />
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1e66ab', fontFamily: 'Poppins' }}>
                            AFP Configuration
                        </Typography>
                    </Box>
                </Box>
                 <Grid2 container spacing={2 } sx={{backgroundColor: '#f7faff',px: 2.5, py: 1}}>
                <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                    <Typography sx={{ color: '#1e66ab', fontWeight: 600, fontSize: '16px', ml: 1 }}>
                        AFP Required
                    </Typography>
                    <Box display="flex" gap={2}>
                        <CustomRadioButton
                            label="Yes"
                            value="true"
                            name="isSelectedAFP"
                            checked={formData.isSelectedAFP === true}
                            onChange={(event) => {
                                const { name, value } = event.target;
                                if (paymentData) {
                                    setRequiredAFPDialog({
                                        isOpen: true,
                                        aFPRequired: value === "true",
                                    });
                                }
                                else {
                                    setFormData((prev) => ({
                                        ...prev,
                                        [name]: value === "true",
                                    }));
                                }
                            }}
                        />
                        <CustomRadioButton
                            label="No"
                            value="false"
                            name="isSelectedAFP"
                            checked={formData.isSelectedAFP === false}
                            onChange={(event) => {
                                const { name, value } = event.target;
                                if (paymentData) {
                                    setRequiredAFPDialog({
                                        isOpen: true,
                                        aFPRequired: value === "true",
                                    });
                                }
                                else {
                                    setFormData((prev) => ({
                                        ...prev,
                                        [name]: value === "true",
                                    }));
                                }
                            }}
                        />
                    </Box>
                </Grid2>

                {/* AFP Type Section (only shows if AFP Required "Yes" is selected) */}
                {formData.isSelectedAFP && (
                    <Grid2 size={{ xs: 12, md: 6, lg: 6 }}>
                        <Typography sx={{ color: '#1e66ab', fontWeight: 600, fontSize: '16px', ml: 1 }}>
                            AFP Type
                        </Typography>
                        <Box display="flex" gap={2}>
                            <CustomRadioButton
                                label="Scheduled"
                                value="true"
                                name="isAFPTypeSchedule"
                                checked={formData.isAFPTypeSchedule == true}
                                onChange={(event) => {
                                    const { name, value } = event.target;
                                    if (paymentData) {
                                        setAFPDialog({
                                            isOpen: true,
                                            aFPValue: value === "true",
                                        });
                                    }
                                    else {
                                        setFormData((prev) => ({
                                            ...prev,
                                            [name]: value === "true",
                                        }));
                                    }
                                }}
                            />
                            <CustomRadioButton
                                label="As Required"
                                value="false"
                                name="isAFPTypeSchedule"
                                checked={formData.isAFPTypeSchedule === false}
                                onChange={(event) => {
                                    const { name, value } = event.target;
                                    if (paymentData) {
                                        setAFPDialog({
                                            isOpen: true,
                                            aFPValue: value === "true",
                                        });
                                    }
                                    else {
                                        setFormData((prev) => ({
                                            ...prev,
                                            [name]: value === "true",
                                        }));
                                    }
                                }}
                            />
                        </Box>
                    </Grid2>
                )}

                {/* (only shows if AFP Type "As Required" ) */}
                {formData.isSelectedAFP && formData.isAFPTypeSchedule === false && (
                    <Grid2
                        container
                        size={{ xs: 12, md: 8, lg: 8 }}
                    >
                        <Grid2 size={{ xs: 12, md: 5, lg: 5 }}>
                            <CustomTextField
                                label="AFP to Certificate"
                                name="afpToCertificate"
                                type="number"
                                value={formData.afpToCertificate}
                                inputProps={{ min: 0, max: 100 }}
                                onChange={(event) => {
                                    const value = Number(event.target.value);
                                    if (value >= 0 && value <= 100) {
                                        setFormData({
                                            ...formData,
                                            afpToCertificate: value,
                                        });
                                    }
                                }}
                                required
                                sx={{ mb: 1,backgroundColor: '#fff' }}
                            />
                            {errors.afpToCertificate && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.afpToCertificate}
                                </Typography>
                            )}
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 5, lg: 5 }}>
                            <CustomTextField
                                label="Invoice to Payment"
                                name="invoiceToPayment"
                                type="number"
                                value={formData.invoiceToPayment}
                                inputProps={{ min: 0, max: 100 }}
                                onChange={(event) => {
                                    const value = Number(event.target.value);
                                    if (value >= 0 && value <= 100) {
                                        setFormData({
                                            ...formData,
                                            invoiceToPayment: value,
                                        });
                                    }
                                }}
                                required
                                sx={{ mb: 1,backgroundColor: '#fff' }}
                            />
                            {errors.invoiceToPayment && (
                                <Typography className="validationMessage" role="alert">
                                    {errors.invoiceToPayment}
                                </Typography>
                            )}
                        </Grid2>

                        <Grid2 size={{ xs: 12, md: 2, lg: 2 }} sx={{ mt: 2 }}>
                            <CustomButton capsule
                                sx={{
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                    width: "100%"
                                }}
                                backgroundColor="#1e66ab"
                                hoverColor="rgba(150, 193, 33, 0.75)"
                                onClick={handleAsRequired}
                            >
                                Save
                            </CustomButton>
                        </Grid2>
                    </Grid2>
                )}

                {/* (only shows if AFP Type "Scheduled" ) */}
                

            </Grid2>
                </Paper>
                {formData.isSelectedAFP && formData.isAFPTypeSchedule && (
                    <AFPTable
                        data={paymentData!}
                        endDate={projectData.endDate}
                        onSaveInterval={handleSchedule}
                    >

                    </AFPTable>
                )}
           

            <CustomConfirmDialog
                open={afpDialog.isOpen}
                title="Change AFP Type"
                message={`You are switching to ${!formData.isAFPTypeSchedule ? 'Scheduled' : 'As Required'}.
                All data currently saved in ${formData.isAFPTypeSchedule ? 'Scheduled' : 'As Required'} will be lost.
                    Are you sure you want to proceed?`}
                onConfirm={handleAFPDialogConfirm}
                onCancel={handleAFPDialogCancel}
                onClose={handleAFPDialogCancel}
                confirmLabel="Yes"
            />

            <CustomConfirmDialog
                open={requiredAFPDialog.isOpen}
                title="Change AFP Requirement"
                message={`Changing "AFP Required" to 'No' 
                    will permanently delete all existing AFP configuration data (if any) 
                    from the database.\nAre you sure you want to proceed?`}
                onConfirm={handleRequiredAFPDialogConfirm}
                onCancel={handleRequiredAFPDialogCancel}
                onClose={handleRequiredAFPDialogCancel}
                confirmLabel="Yes"
            />

            <CustomBackdrop open={loading} />
            <Toaster open={toast.open} message={toast.message} type={toast.type as "success" | "error"} onClose={() => setToast({ ...toast, open: false })} />
        </Box>
    );
};

export default AFP;
